import { useQuery } from '@tanstack/react-query';
import { useRouter } from 'next/router';
import { useEffect } from 'react';

import { MAX_SWAP_FEE_BPS } from '@/constants/referrers';

import { referrerIdSelector, referrerNameSelector, useReferrerStore } from '@/store/referrals';
import { useSwapFeeStore } from '@/store/swapFee';

import { fetcher } from '@/utils/fetcher';
import { EthereumAddress, isEthereumAddress } from '@/utils/models';
import { routes } from '@/utils/routes';

import { useTokenFeeConfig } from '../useTokenFeeConfig';

interface SwapFeeInfo {
  integratorName: string | undefined;
  swapFeeBps: number | undefined;
  swapFeeRecipient: EthereumAddress | undefined;
  swapFeeToken: EthereumAddress | undefined;
}

/**
 * Parse the swap fee bps and swap fee recipient from the query string
 * then update the values in the store.
 *
 * @remarks
 *
 * The swap fee query parameters are to be used for the duration of the users session.
 * This is to ensure that if a user comes from a referral url,
 * the fee persists even if the user navigates to other pages.
 */
export const useSwapFeeQuery = ({
  buyTokenAddress,
  buyTokenChainId,
  sellTokenAddress,
  sellTokenChainId,
}: {
  buyTokenAddress?: EthereumAddress;
  sellTokenAddress?: EthereumAddress;
  buyTokenChainId?: number;
  sellTokenChainId?: number;
}): SwapFeeInfo => {
  const router = useRouter();
  const {
    swapFeeBps,
    swapFeeRecipient,
    setSwapFeeBps,
    setSwapFeeRecipient,
    swapFeeToken,
    setSwapFeeToken,
  } = useSwapFeeStore();
  const { query } = router;
  const { referrerId } = useReferrerStore(referrerIdSelector);
  const { referrerName, setReferrerName } = useReferrerStore(referrerNameSelector);

  // Fetch the fee token config to get the fee token with the highest priority
  const { data: config } = useTokenFeeConfig({
    tokenAddressA: sellTokenAddress,
    chainIdA: sellTokenChainId,
    tokenAddressB: buyTokenAddress,
    chainIdB: buyTokenChainId,
  });

  // If there is no swap fee token query parameter, use the token with highest fee priority.
  // Or fallback to the buy token address.
  const defaultFeeToken = config?.feeToken || buyTokenAddress;

  // Use the referrer Id to fetch info about the referrer,
  // which includes the `swapFeeRecipient` address
  const referrerInfoUrl = referrerId ? `${routes.api.REFERRAL_INFO}?ref=${referrerId}` : null;
  const { data } = useQuery<{ swapFeeRecipient: EthereumAddress; integratorName: string }>({
    queryKey: ['referrer-info', referrerId],
    queryFn: () => fetcher(referrerInfoUrl!),
    enabled: !!referrerInfoUrl,
    refetchOnReconnect: false,
  });

  /**
   * Set swap fee bps
   */
  useEffect(() => {
    // Set the swap fee bps from a url query parameter `swapFeeBps`.
    // It is added to the store so it persists even after
    // navigating away from the url with the `swapFeeBps` query parameter
    if (typeof swapFeeBps === 'undefined' && query.swapFeeBps && !Array.isArray(query.swapFeeBps)) {
      const newSwapFeeBps = parseInt(query.swapFeeBps, 10);
      if (Number.isInteger(newSwapFeeBps))
        setSwapFeeBps(Math.max(0, Math.min(MAX_SWAP_FEE_BPS, newSwapFeeBps)));
    }
  }, [swapFeeBps, query.swapFeeBps, query.swapFeeRecipient, setSwapFeeBps, setSwapFeeRecipient]);

  /**
   * Set swap fee recipient
   */
  useEffect(() => {
    if (data && isEthereumAddress(data?.swapFeeRecipient)) {
      setSwapFeeRecipient(data.swapFeeRecipient);
      setReferrerName(data.integratorName);
    }
  }, [data, data?.swapFeeRecipient, data?.integratorName, setSwapFeeRecipient, setReferrerName]);

  /**
   * Set swap fee token
   */
  useEffect(() => {
    // Let the swap fee token be overriden in the same session via a new query parameter
    if (
      query.swapFeeToken &&
      !Array.isArray(query.swapFeeToken) &&
      isEthereumAddress(query.swapFeeToken)
    ) {
      setSwapFeeToken(query.swapFeeToken);
    }
  }, [query.swapFeeToken, setSwapFeeToken]);

  const hasRequiredSwapFeeParams = !!swapFeeBps && !!swapFeeRecipient;

  if (!hasRequiredSwapFeeParams) {
    return {
      swapFeeBps: undefined,
      swapFeeRecipient: undefined,
      swapFeeToken: undefined,
      integratorName: undefined,
    };
  }

  return {
    swapFeeBps,
    swapFeeRecipient,
    // Swap fee token can only be either the buy or sell token.
    swapFeeToken:
      (swapFeeToken === buyTokenAddress || swapFeeToken === sellTokenAddress) &&
      swapFeeToken &&
      isEthereumAddress(swapFeeToken)
        ? swapFeeToken
        : defaultFeeToken,
    integratorName: referrerName,
  };
};
